/**
 * Auto Translator
 * Automatically translates block-level elements in DOM content.
 * Uses ContentUnit model for key parity with WordPress TS, PHP, and edge proxy.
 *
 * Missing translations are reported via the event bus (translation:not-found),
 * picked up by MissingTranslationQueue, and synced through NetworkRepository.
 */

import type { LionRapidCore } from '../lionRapid.js';
import type { ContentUnit } from '../content/types.js';
import { BlockExtractor, ExtractedBlock } from './block-extractor.js';
import { AttributeExtractor, ExtractedAttribute } from './attribute-extractor.js';
import { ExcludePatterns } from './exclude-filter.js';
import { DOMObserver } from './dom-observer.js';
import { HTMLSerializer } from '../content/serializer/HTMLSerializer.js';
import { generateAutoKey } from './md5.js';

const htmlSerializer = new HTMLSerializer();

export interface AutoTranslatorConfig {
  excludePatterns?: Partial<ExcludePatterns>;
  excludeSelectors?: string[];
  minTextLength?: number;
  debounceMs?: number;
}

export class AutoTranslator {
  private core: LionRapidCore | null = null;
  private blockExtractor: BlockExtractor;
  private attributeExtractor: AttributeExtractor;
  private domObserver: DOMObserver | null = null;
  private locale = '';
  private sourceLocale = '';
  private namespace = '';
  private debounceMs: number;
  private pendingElements: Set<Element> = new Set();
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private translatedCount = 0;

  constructor(config: AutoTranslatorConfig = {}) {
    this.blockExtractor = new BlockExtractor(
      config.excludePatterns,
      config.excludeSelectors,
      config.minTextLength
    );
    this.attributeExtractor = new AttributeExtractor(
      config.excludePatterns,
      config.excludeSelectors,
      config.minTextLength
    );
    this.debounceMs = config.debounceMs ?? 100;
  }

  /**
   * Set the core instance
   */
  setCore(core: LionRapidCore): void {
    this.core = core;
  }

  /**
   * Initialize locale and namespace for cache key construction
   */
  init(config: { namespace: string; locale: string; sourceLocale: string }): void {
    this.locale = config.locale;
    this.sourceLocale = config.sourceLocale;
    this.namespace = config.namespace;
  }

  /**
   * Update locale (after language change)
   */
  updateLocale(locale: string): void {
    this.locale = locale;
  }

  /**
   * Check if ready
   */
  isReady(): boolean {
    return this.core !== null;
  }

  /**
   * Generate auto key for text (exposed for testing)
   */
  generateAutoKey(text: string): string {
    return generateAutoKey(text);
  }

  /**
   * Queue an element for translation (debounced)
   */
  queueElement(element: Element): void {
    this.pendingElements.add(element);
    this.scheduleProcessing();
  }

  /**
   * Translate an element immediately
   */
  translateElement(element: Element): number {
    if (!this.core) {
      return 0;
    }

    const blockCount = this.translateBlocks(this.blockExtractor.extract(element));
    const attrCount = this.translateAttributesIn(this.attributeExtractor.extract(element));
    return blockCount + attrCount;
  }

  /**
   * Translate the entire page
   */
  translatePage(): number {
    return this.translateElement(document.body);
  }

  /**
   * Start observing DOM for dynamic content
   */
  observe(): void {
    if (this.domObserver) {
      return;
    }

    this.domObserver = new DOMObserver({
      onNewContent: (element) => this.queueElement(element),
    });
    this.domObserver.start();
  }

  /**
   * Stop observing DOM
   */
  stopObserving(): void {
    if (this.domObserver) {
      this.domObserver.stop();
      this.domObserver = null;
    }
  }

  /**
   * Schedule debounced processing
   */
  private scheduleProcessing(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.processPendingElements();
    }, this.debounceMs);
  }

  /**
   * Process all pending elements
   */
  private processPendingElements(): void {
    if (!this.core || this.pendingElements.size === 0) {
      return;
    }

    let totalTranslated = 0;

    for (const element of this.pendingElements) {
      // Skip if element was removed from DOM
      if (!document.body.contains(element)) {
        continue;
      }

      const count = this.translateElement(element);
      totalTranslated += count;
    }

    this.pendingElements.clear();
    this.translatedCount += totalTranslated;
  }

  /**
   * Translate extracted blocks using ContentUnit model
   */
  private translateBlocks(extracted: ExtractedBlock[]): number {
    if (!this.core) return 0;

    let count = 0;

    for (const { element, unit, key } of extracted) {
      // Construct full cache key: locale:namespace:key
      const fullKey = `${this.locale}:${this.namespace}:${key}`;

      // Look up translation from cache
      const cached = this.core.cache.get(fullKey);

      if (cached && cached.value !== unit.text && cached.value !== key) {
        // Translation found — save original HTML before first translation
        if (!element.hasAttribute('data-lr-original')) {
          element.setAttribute('data-lr-original', element.innerHTML);
        }

        // Source styles win when the cached translation lacks them. Mirrors
        // the edge proxy's `{...translation.styles, ...source.styles}` merge
        // so a stale plain-text cache entry can't strip inline wrappers
        // (`<span class="...">`, `<b>`, `<a>`) that the source has.
        const cacheHasStyles = cached.styles && Object.keys(cached.styles).length > 0;
        const sourceHasStyles = Object.keys(unit.styles).length > 0;
        const hasIndexedMarkers = /<\/?[0-9]+\/?>/.test(cached.value);

        if (sourceHasStyles && !hasIndexedMarkers) {
          // Cache value is missing the indexed-tag structure needed to
          // re-attach the source styles. Don't clobber the DOM — leave
          // whatever's currently rendered (e.g. edge-rendered styled HTML)
          // and just mark the element so we don't reprocess it.
        } else {
          const styles = cacheHasStyles ? cached.styles! : unit.styles;
          const translatedUnit: ContentUnit = { text: cached.value, styles };
          element.innerHTML = htmlSerializer.serialize(translatedUnit);
        }
        element.setAttribute('data-lr-block', key);
        element.setAttribute('data-lionrapid-client', '1');
        count++;
      } else {
        // No translation — report missing via event bus
        // MissingTranslationQueue picks this up and syncs through NetworkRepository
        this.core.bus.emit('translation:not-found', {
          payload: {
            key,
            fallbackValue: unit.text,
            locale: this.sourceLocale,
            namespace: this.namespace,
            status: 404 as const,
            styles: unit.styles,
          },
        });
      }
    }

    return count;
  }

  /**
   * Translate extracted attribute units against the cache.
   *
   * Mirrors translateBlocks but for attribute values. For each hit, the
   * original attribute value is preserved in `data-lr-orig-{name}` so
   * retranslate() can restore source. The element accumulates a comma-
   * separated `data-lionrapid-client-attrs` list naming every attr the
   * client touched — so we can find them all on retranslate without
   * iterating a fixed selector set.
   *
   * Cache misses emit `translation:not-found` with empty styles, so the
   * existing MissingTranslationQueue + NetworkRepository sync queue
   * picks them up unchanged.
   */
  private translateAttributesIn(extracted: ExtractedAttribute[]): number {
    if (!this.core) return 0;
    let count = 0;

    for (const { element, attrName, unit, key } of extracted) {
      const fullKey = `${this.locale}:${this.namespace}:${key}`;
      const cached = this.core.cache.get(fullKey);

      if (cached && cached.value !== unit.text && cached.value !== key) {
        const lower = attrName.toLowerCase();
        const origAttr = `data-lr-orig-${lower}`;
        // Preserve the source value the first time we touch this attr,
        // so retranslate() can restore it even after multiple in-page
        // language switches.
        if (!element.hasAttribute(origAttr)) {
          element.setAttribute(origAttr, unit.text);
        }
        element.setAttribute(attrName, cached.value);
        element.setAttribute(`data-lr-attr-${lower}`, key);

        // Append to the client-attrs list (dedup so multiple passes
        // don't grow it without bound).
        const existing = element.getAttribute('data-lionrapid-client-attrs') ?? '';
        const set = new Set(existing.split(',').filter(Boolean));
        set.add(lower);
        element.setAttribute('data-lionrapid-client-attrs', Array.from(set).join(','));

        count++;
      } else {
        this.core.bus.emit('translation:not-found', {
          payload: {
            key,
            fallbackValue: unit.text,
            locale: this.sourceLocale,
            namespace: this.namespace,
            status: 404 as const,
            styles: {},
          },
        });
      }
    }

    return count;
  }

  /**
   * Get translated count
   */
  getTranslatedCount(): number {
    return this.translatedCount;
  }

  /**
   * Restore every client-translated attribute on `el` from its
   * `data-lr-orig-*` backup, then strip the stamps. Mirrors the block-side
   * restoration in retranslate() so language switch / editor reset clears
   * attributes too.
   */
  private restoreClientAttributes(el: Element): void {
    const list = el.getAttribute('data-lionrapid-client-attrs');
    if (list === null) return;
    for (const name of list.split(',').filter(Boolean)) {
      const orig = el.getAttribute(`data-lr-orig-${name}`);
      if (orig !== null) {
        el.setAttribute(name, orig);
        el.removeAttribute(`data-lr-orig-${name}`);
      }
      el.removeAttribute(`data-lr-attr-${name}`);
    }
    el.removeAttribute('data-lionrapid-client-attrs');
  }

  /**
   * Re-translate a single element (for editor live preview reset)
   */
  retranslateElement(element: Element): void {
    const original = element.getAttribute('data-lr-original');
    if (original !== null) element.innerHTML = original;
    element.removeAttribute('data-lionrapid-client');
    element.removeAttribute('data-lr-block');
    this.restoreClientAttributes(element);
    this.translateElement(element);
  }

  /**
   * Re-translate the page (after language change)
   */
  retranslate(): void {
    // Restore original HTML for all previously translated blocks
    const translatedBlocks = document.querySelectorAll('[data-lionrapid-client]');
    translatedBlocks.forEach((el) => {
      const original = el.getAttribute('data-lr-original');
      if (original !== null) {
        el.innerHTML = original;
      }
      el.removeAttribute('data-lionrapid-client');
      el.removeAttribute('data-lr-block');
    });

    // Restore source values for client-translated attributes. Selector
    // matches the marker the attribute pass writes; per-name lists live
    // inside that attribute so we don't need a fixed selector per kind.
    const translatedAttrs = document.querySelectorAll('[data-lionrapid-client-attrs]');
    translatedAttrs.forEach((el) => this.restoreClientAttributes(el));

    // Re-translate (blocks are now back to source text, so auto_keys will be correct)
    this.translatedCount = 0;
    if (this.locale !== this.sourceLocale) {
      this.translatePage();
    }
  }
}
